// Components/PrintStudentsModal.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { Printer, FilePdf, FileExcel } from 'react-bootstrap-icons';
import { studentAPI } from '../services/api';
import { useReactToPrint } from 'react-to-print';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import '../CSS/PrintStudentsModal.css';

const PrintStudentsModal = ({ show, onHide, category }) => {
  const [selectedValue, setSelectedValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [studentData, setStudentData] = useState(null);
  const [error, setError] = useState('');
  const [uniqueValues, setUniqueValues] = useState([]);
  const [loadingValues, setLoadingValues] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const printRef = useRef();
  const abortControllerRef = useRef(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (show) {
      setSelectedValue('');
      setStudentData(null);
      setError('');
      setRetryCount(0);
      fetchUniqueValues();
    }
    
    // Cleanup function to abort any pending requests
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [show, category]);

  const fetchUniqueValues = async () => {
    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();
    
    setLoadingValues(true);
    setError('');
    
    try {
      // Add timeout and signal to the request
      const response = await studentAPI.getStudents({ 
        limit: 1000,
        signal: abortControllerRef.current.signal
      });
      
      const students = response.data.data;
      
      let values = [];
      switch(category) {
        case 'constituency':
          values = [...new Set(students.map(s => s.constituency).filter(Boolean))];
          break;
        case 'region':
          values = [...new Set(students.map(s => s.region).filter(Boolean))];
          break;
        case 'religion':
          values = [...new Set(students.map(s => s.religion).filter(Boolean))];
          break;
        default:
          values = [];
      }
      
      setUniqueValues(values.sort());
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        console.log('Request canceled');
        return;
      }
      
      console.error('Error fetching unique values:', err);
      
      // Auto-retry up to 3 times
      if (retryCount < 3) {
        setRetryCount(prev => prev + 1);
        setTimeout(() => fetchUniqueValues(), 2000 * Math.pow(2, retryCount));
        setError(`Loading failed. Retrying... (${retryCount + 1}/3)`);
      } else {
        setError('Failed to load options. Please check your connection and try again.');
      }
    } finally {
      setLoadingValues(false);
    }
  };

  const handleValueChange = (e) => {
    setSelectedValue(e.target.value);
    setStudentData(null);
    setError('');
  };

  const fetchStudents = async () => {
    if (!selectedValue) {
      setError(`Please select a ${category}`);
      return;
    }

    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError('');
    setRetryCount(0);
    
    try {
      console.log(`Fetching students for ${category}:`, selectedValue);
      
      let response;
      switch(category) {
        case 'constituency':
          response = await studentAPI.getStudentsByConstituency(selectedValue);
          break;
        case 'region':
          response = await studentAPI.getStudentsByRegion(selectedValue);
          break;
        case 'religion':
          response = await studentAPI.getStudentsByReligion(selectedValue);
          break;
        default:
          return;
      }
      
      if (response.data && response.data.success) {
        if (response.data.data.length === 0) {
          setError(`No students found for this ${category}`);
          setStudentData(null);
        } else {
          setStudentData(response.data);
        }
      } else {
        setError(`No students found for this ${category}`);
        setStudentData(null);
      }
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        return;
      }
      
      console.error('Error fetching students:', err);
      
      // Auto-retry logic
      if (retryCount < 2) {
        setRetryCount(prev => prev + 1);
        setTimeout(() => fetchStudents(), 3000);
        setError(`Loading failed. Retrying... (${retryCount + 1}/3)`);
      } else {
        setError(err.response?.data?.error || `Failed to fetch students. Please try again.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `${category}_${selectedValue}_Students_${new Date().toLocaleDateString()}`,
    onAfterPrint: () => console.log('Print completed'),
  });

  const generatePDF = () => {
    if (!studentData?.data) return;

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm'
    });

    // Title
    doc.setFontSize(18);
    doc.setTextColor(41, 128, 185);
    
    let title;
    switch(category) {
      case 'constituency':
        title = `${selectedValue} Constituency - Students List`;
        break;
      case 'region':
        title = `${selectedValue} Region - Students List`;
        break;
      case 'religion':
        title = `${selectedValue} - Students List`;
        break;
    }
    doc.text(title, 14, 15);
    
    // Date and Statistics
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 22);
    doc.text(`Total Students: ${studentData.statistics.total}`, 14, 27);
    doc.text(`Male: ${studentData.statistics.maleCount} | Female: ${studentData.statistics.femaleCount}`, 14, 32);

    // Table headers
    const headers = [
      ['No.', 'Student Name', 'Gender', 'Class', 'Date of Birth', 'Phone', 'Father\'s Name', 'Mother\'s Name', 'Village', 'District']
    ];

    const tableData = studentData.data.map((student, index) => [
      index + 1,
      student.studentName || 'N/A',
      student.gender || 'N/A',
      student.class || 'N/A',
      student.dateOfBirth ? new Date(student.dateOfBirth).toLocaleDateString() : 'N/A',
      student.phoneNumber || 'N/A',
      student.fatherName || 'N/A',
      student.motherName || 'N/A',
      student.village || 'N/A',
      student.district || 'N/A'
    ]);

    doc.autoTable({
      head: headers,
      body: tableData,
      startY: 40,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 245, 245] }
    });

    doc.save(`${category}_${selectedValue}_Students_${new Date().toISOString().slice(0,10)}.pdf`);
  };

  const generateExcel = () => {
    if (!studentData?.data) return;

    const workbook = XLSX.utils.book_new();
    
    const formattedData = studentData.data.map((student, index) => ({
      'No.': index + 1,
      'Student Name': student.studentName || 'N/A',
      'Gender': student.gender || 'N/A',
      'Class': student.class || 'N/A',
      'Date of Birth': student.dateOfBirth ? new Date(student.dateOfBirth).toLocaleDateString() : 'N/A',
      'Phone': student.phoneNumber || 'N/A',
      "Father's Name": student.fatherName || 'N/A',
      "Mother's Name": student.motherName || 'N/A',
      'Village': student.village || 'N/A',
      'District': student.district || 'N/A',
      'Province': student.province || 'N/A',
      'Region': student.region || 'N/A',
      'Religion': student.religion || 'N/A',
      'Constituency': student.constituency || 'N/A'
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    
    // Set column widths
    worksheet['!cols'] = [
      { wch: 5 }, { wch: 25 }, { wch: 8 }, { wch: 6 }, { wch: 12 },
      { wch: 15 }, { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 15 },
      { wch: 15 }, { wch: 15 }, { wch: 20 }
    ];

    XLSX.utils.book_append_sheet(workbook, worksheet, `${category}_${selectedValue}`);
    XLSX.writeFile(workbook, `${category}_${selectedValue}_Students_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const PrintableContent = () => (
    <div ref={printRef} className="printable-content">
      <style>{`
        @media print {
          body { font-family: Arial, sans-serif; }
          .print-header { text-align: center; margin-bottom: 20px; }
          .print-stats { margin: 15px 0; text-align: center; }
          .print-table { width: 100%; border-collapse: collapse; }
          .print-table th { background-color: #2c3e50; color: white; padding: 10px; font-size: 12px; }
          .print-table td { border: 1px solid #ddd; padding: 8px; font-size: 11px; }
          .print-table tr:nth-child(even) { background-color: #f9f9f9; }
          .print-footer { text-align: center; margin-top: 20px; font-size: 10px; color: #666; }
        }
      `}</style>
      
      <div className="print-header">
        <h1>
          {category === 'constituency' && `${selectedValue} Constituency`}
          {category === 'region' && `${selectedValue} Region`}
          {category === 'religion' && `${selectedValue}`} - Students List
        </h1>
        <h3>{new Date().toLocaleDateString()}</h3>
      </div>

      <div className="print-stats">
        <p>
          Total Students: {studentData?.statistics.total} | 
          Male: {studentData?.statistics.maleCount} | 
          Female: {studentData?.statistics.femaleCount}
        </p>
      </div>

      <table className="print-table">
        <thead>
          <tr>
            <th>No.</th>
            <th>Student Name</th>
            <th>Gender</th>
            <th>Class</th>
            <th>Date of Birth</th>
            <th>Phone</th>
            <th>Father's Name</th>
            <th>Mother's Name</th>
            <th>Village</th>
            <th>District</th>
          </tr>
        </thead>
        <tbody>
          {studentData?.data.map((student, index) => (
            <tr key={student.id}>
              <td>{index + 1}</td>
              <td>{student.studentName}</td>
              <td>{student.gender}</td>
              <td>{student.class}</td>
              <td>{new Date(student.dateOfBirth).toLocaleDateString()}</td>
              <td>{student.phoneNumber}</td>
              <td>{student.fatherName}</td>
              <td>{student.motherName}</td>
              <td>{student.village}</td>
              <td>{student.district}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="print-footer">
        <p>Generated by School Management System</p>
      </div>
    </div>
  );

  const getCategoryLabel = () => {
    switch(category) {
      case 'constituency': return 'Constituency';
      case 'region': return 'Region';
      case 'religion': return 'Religion';
      default: return '';
    }
  };

  const handleRetry = () => {
    setRetryCount(0);
    fetchUniqueValues();
  };

 return (
    <Modal show={show} onHide={onHide} size="lg" centered dialogClassName="psm-modal-dialog">
      <Modal.Header closeButton className="psm-modal-header">
        <Modal.Title className="psm-modal-title">
          <Printer className="psm-title-icon" />
          Print Students by {getCategoryLabel()}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="psm-modal-body">

        {/* ── Category selector ── */}
        <div className="psm-field-group">
          <label className="psm-field-label">Select {getCategoryLabel()}</label>
          <div className="psm-select-row">
            <select
              value={selectedValue}
              onChange={handleValueChange}
              className="psm-select"
              disabled={loadingValues}
            >
              <option value="">
                {loadingValues ? 'Loading options…' : `Choose a ${getCategoryLabel().toLowerCase()}…`}
              </option>
              {uniqueValues.map(value => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>

            {error && error.includes('Retrying') && (
              <button className="psm-btn psm-btn-retry" onClick={handleRetry}>
                Retry Now
              </button>
            )}
          </div>

          {/* Options loading hint */}
          {loadingValues && (
            <div className="psm-options-loading">
              <span className="psm-spinner" aria-hidden="true" />
              Loading options… This may take a moment on slow connections.
            </div>
          )}
        </div>

        {/* ── Action buttons ── */}
        <div className="psm-action-row">
          <button
            className="psm-btn psm-btn-load"
            onClick={fetchStudents}
            disabled={!selectedValue || loading || loadingValues}
          >
            {loading ? (
              <>
                <span className="psm-spinner" aria-hidden="true" />
                Loading…
              </>
            ) : (
              'Load Students'
            )}
          </button>

          {studentData && studentData.data.length > 0 && (
            <>
              <button className="psm-btn psm-btn-print" onClick={handlePrint}>
                <Printer className="psm-btn-icon" /> Print
              </button>
              <button className="psm-btn psm-btn-pdf" onClick={generatePDF}>
                <FilePdf className="psm-btn-icon" /> PDF
              </button>
              <button className="psm-btn psm-btn-excel" onClick={generateExcel}>
                <FileExcel className="psm-btn-icon" /> Excel
              </button>
            </>
          )}
        </div>

        {/* ── Error / retry alert ── */}
        {error && (
          <div className={`psm-alert ${error.includes('Retrying') ? 'psm-alert-warn' : 'psm-alert-danger'}`}>
            <span className="psm-alert-text">{error}</span>
            <button className="psm-alert-close" onClick={() => setError('')} aria-label="Dismiss">×</button>
          </div>
        )}

        {/* ── Loading indicator ── */}
        {loading && (
          <div className="psm-loading-state">
            <span className="psm-spinner psm-spinner-lg" aria-hidden="true" />
            <p className="psm-loading-text">
              Loading students… This may take a moment on slow connections.
            </p>
          </div>
        )}

        {/* ── Preview table ── */}
        {studentData && studentData.data.length > 0 && !loading && (
          <div className="psm-preview-section">
            <h5 className="psm-preview-title">
              Preview — {getCategoryLabel()}: {selectedValue}
            </h5>
            <p className="psm-preview-subtitle">
              Showing first 10 of {studentData.data.length} students
            </p>

            <div className="psm-table-wrapper">
              <table className="psm-table">
                <thead className="psm-table-header">
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Gender</th>
                    <th>Class</th>
                    <th>Phone</th>
                  </tr>
                </thead>
                <tbody className="psm-table-body">
                  {studentData.data.slice(0, 10).map((student, index) => (
                    <tr key={student.id}>
                      <td>{index + 1}</td>
                      <td className="psm-cell-name">{student.studentName}</td>
                      <td>
                        <span className={`psm-gender-badge psm-gender-${student.gender?.toLowerCase()}`}>
                          {student.gender}
                        </span>
                      </td>
                      <td>
                        <span className="psm-class-badge">Gr. {student.class}</span>
                      </td>
                      <td>{student.phoneNumber}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {studentData.data.length > 10 && (
                <p className="psm-table-overflow-note">
                  … and {studentData.data.length - 10} more students
                </p>
              )}
            </div>

            {/* Stats strip */}
            <div className="psm-stats-strip">
              <span className="psm-stat-item">
                Total <strong>{studentData.statistics.total}</strong>
              </span>
              <span className="psm-stat-divider" />
              <span className="psm-stat-item">
                Male <strong>{studentData.statistics.maleCount}</strong>
              </span>
              <span className="psm-stat-divider" />
              <span className="psm-stat-item">
                Female <strong>{studentData.statistics.femaleCount}</strong>
              </span>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="psm-modal-footer">
        <button className="psm-btn psm-btn-close-modal" onClick={onHide}>Close</button>
      </Modal.Footer>

      {/* Hidden printable content — unchanged */}
      <div style={{ display: 'none' }}>
        <PrintableContent />
      </div>
    </Modal>
  );
};

export default PrintStudentsModal;